import { useEffect, useRef, useState } from "react";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Code, Link2, Undo2, Redo2, Underline } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Minimal contentEditable-based WYSIWYG.
 * Emits HTML via onChange.
 */
export default function RichTextEditor({ value, onChange, placeholder = "Write your post…" }) {
  const ref = useRef(null);
  const [, force] = useState(0);

  useEffect(() => {
    if (ref.current && value !== ref.current.innerHTML) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    onChange?.(ref.current?.innerHTML || "");
    force((n) => n + 1);
  };

  const setHeading = (tag) => exec("formatBlock", tag);

  const insertLink = () => {
    const url = window.prompt("Enter URL");
    if (url) exec("createLink", url);
  };

  const handleInput = () => onChange?.(ref.current?.innerHTML || "");

  const btnCls =
    "h-8 w-8 grid place-items-center border border-border rounded-sm hover:bg-foreground hover:text-background transition-colors";

  return (
    <div className="border border-border rounded-sm bg-background">
      <div className="flex flex-wrap items-center gap-1 border-b border-border px-2 py-2 bg-secondary/40">
        <button type="button" className={btnCls} onClick={() => exec("bold")} title="Bold" data-testid="rt-bold"><Bold className="h-3.5 w-3.5" /></button>
        <button type="button" className={btnCls} onClick={() => exec("italic")} title="Italic" data-testid="rt-italic"><Italic className="h-3.5 w-3.5" /></button>
        <button type="button" className={btnCls} onClick={() => exec("underline")} title="Underline"><Underline className="h-3.5 w-3.5" /></button>
        <span className="w-px h-5 bg-border mx-1" />
        <button type="button" className={btnCls} onClick={() => setHeading("h2")} title="H2" data-testid="rt-h2"><Heading2 className="h-3.5 w-3.5" /></button>
        <button type="button" className={btnCls} onClick={() => setHeading("h3")} title="H3"><Heading3 className="h-3.5 w-3.5" /></button>
        <button type="button" className={btnCls} onClick={() => setHeading("p")} title="Paragraph"><span className="text-[11px] font-mono">P</span></button>
        <span className="w-px h-5 bg-border mx-1" />
        <button type="button" className={btnCls} onClick={() => exec("insertUnorderedList")} title="Bullet list"><List className="h-3.5 w-3.5" /></button>
        <button type="button" className={btnCls} onClick={() => exec("insertOrderedList")} title="Numbered list"><ListOrdered className="h-3.5 w-3.5" /></button>
        <button type="button" className={btnCls} onClick={() => setHeading("blockquote")} title="Quote"><Quote className="h-3.5 w-3.5" /></button>
        <button type="button" className={btnCls} onClick={() => setHeading("pre")} title="Code block"><Code className="h-3.5 w-3.5" /></button>
        <button type="button" className={btnCls} onClick={insertLink} title="Link"><Link2 className="h-3.5 w-3.5" /></button>
        <span className="w-px h-5 bg-border mx-1" />
        <button type="button" className={btnCls} onClick={() => exec("undo")} title="Undo"><Undo2 className="h-3.5 w-3.5" /></button>
        <button type="button" className={btnCls} onClick={() => exec("redo")} title="Redo"><Redo2 className="h-3.5 w-3.5" /></button>
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={handleInput}
        onBlur={handleInput}
        className="prose-editorial min-h-[280px] max-h-[600px] overflow-y-auto px-4 py-3 outline-none text-sm"
        data-placeholder={placeholder}
        data-testid="rt-editor"
        suppressContentEditableWarning
      />
      <style>{`
        [contenteditable][data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: hsl(var(--muted-foreground));
        }
      `}</style>
      <div className="border-t border-border px-3 py-2 flex justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange?.("")}>Clear</Button>
      </div>
    </div>
  );
}
